/**
 * RFC-4122 v4 UUID generation that also works in insecure contexts.
 *
 * `crypto.randomUUID()` is exposed only in secure contexts (HTTPS,
 * http://localhost, http://127.0.0.1). When the app is served over plain HTTP
 * on a named host — e.g. http://tgxwiki:3020 — `crypto.randomUUID` is
 * `undefined`, so calling it throws `TypeError: crypto.randomUUID is not a
 * function`. `crypto.getRandomValues()` is NOT secure-context gated, so we use
 * it as the fallback and only resort to `Math.random` when Web Crypto is
 * entirely unavailable.
 */
export function generateUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // Set the version (4) and variant (10xx) bits per RFC 4122 §4.4.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return (
    hex.slice(0, 4).join("") + "-" +
    hex.slice(4, 6).join("") + "-" +
    hex.slice(6, 8).join("") + "-" +
    hex.slice(8, 10).join("") + "-" +
    hex.slice(10, 16).join("")
  );
}
